import DocumentationButton from "components/DocumentationButton";
import { EmptystateKeyColored } from "components/icons/generated";
import usePersonalApiKeysState from "components/ApiKeys/PersonalApiKeys/usePersonalApiKeysState";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import Tooltip from "ds/components/Tooltip";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import useCanManageApiKeys from "shared/ApiKey/access/useCanManageApiKeys";
import { SeatLimit } from "types/generated";
import { isSaasDistribution } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";
import { SEAT_LIMITS_TOOLTIP } from "views/Account/Settings/SeatLimitsNotification/constants";
import useSeatLimit from "views/Account/Settings/SeatLimitsNotification/useSeats";

const TURNED_OFF_TITLE = "Personal API keys are turned off for this account";

const isSaas = isSaasDistribution();

type PersonalSettingsApiKeysEmptyProps =
  | {
      isUnavailable: true;
    }
  | {
      isUnavailable?: false;
      onCreate: () => void;
      seats?: SeatLimit;
    };

const PersonalSettingsApiKeysEmpty = (props: PersonalSettingsApiKeysEmptyProps) => {
  const seats = props.isUnavailable ? undefined : props.seats;
  const { isLimitReached } = useSeatLimit(seats);
  const { enabled, setEnabled, requestEnable } = usePersonalApiKeysState();
  const isTurnedOff = enabled === false;
  const canManageApiKeys = useCanManageApiKeys();

  const documentationButton = (
    <DocumentationButton
      to={getDocsUrl("/integrations/personal-api-keys.html")}
      label="Documentation"
      analyticsPage={AnalyticsPagePersonal.PersonalApiKeys}
      analyticsTitle="Empty state - Documentation Clicked"
    />
  );

  if (props.isUnavailable) {
    return (
      <Box align="center" justify="center" grow="1">
        <EmptyState
          title="Personal API keys aren’t available on this organization"
          icon={EmptystateKeyColored}
          caption="Once your organization moves to Identity Access Management, you’ll be able to create keys that use your own permissions."
        />
      </Box>
    );
  }

  if (isTurnedOff && canManageApiKeys) {
    return (
      <Box align="center" justify="center" grow="1">
        <EmptyState
          title={TURNED_OFF_TITLE}
          icon={EmptystateKeyColored}
          caption="Personal API keys let everyone in your account use Spacelift from the CLI, Terraform and the API with their own permissions. Each active key uses an API key seat."
        >
          <Box direction="row" gap="medium">
            {documentationButton}
            <Button onPress={() => setEnabled(true)} variant="primary">
              Enable
            </Button>
          </Box>
        </EmptyState>
      </Box>
    );
  }

  if (isTurnedOff) {
    return (
      <Box align="center" justify="center" grow="1">
        <EmptyState
          title={TURNED_OFF_TITLE}
          icon={EmptystateKeyColored}
          caption="An account admin hasn't enabled Personal API keys yet. Once they do, you can create keys that use your own permissions from the CLI, Terraform and the API."
        >
          <Box direction="row" gap="medium">
            {documentationButton}
            {isSaas && (
              <Button onPress={requestEnable} variant="primary">
                Ask your admin to enable
              </Button>
            )}
          </Box>
        </EmptyState>
      </Box>
    );
  }

  return (
    <Box align="center" justify="center" grow="1">
      <EmptyState
        title="You don't have any Personal API keys yet"
        icon={EmptystateKeyColored}
        caption="Personal API keys let you use Spacelift from the CLI, Terraform and the API with your own permissions."
      >
        <Tooltip
          active={isLimitReached}
          on={(tooltipProps) => (
            <Button
              onPress={props.onCreate}
              variant="primary"
              disabled={isLimitReached}
              {...tooltipProps}
            >
              Create API key
            </Button>
          )}
        >
          {SEAT_LIMITS_TOOLTIP}
        </Tooltip>
      </EmptyState>
    </Box>
  );
};

export default PersonalSettingsApiKeysEmpty;
